import api from '../../../services/api';
import {
  normalizeOrderSettlement,
  normalizeOrderSettlementCommandResult,
  normalizeOrderSettlementCommandSubmitResult,
  normalizeOrderSettlementReadiness,
  type OrderSettlementCommandResultDto,
  type OrderSettlementCommandSubmitResultDto,
  type OrderSettlementDto,
  type OrderSettlementReadinessDto,
} from '../../../dto/orderSettlement';

/**
 * OPERATIONS binds only the frozen physical tranche. ADMIN binds that same
 * tranche plus the finance preview it is allowed to inspect. Keeping the union
 * disjoint prevents OPERATIONS from accidentally attaching a finance token.
 */
export interface OperationalOrderSettlementCommand {
  requestKey: string;
  recognitionMonth?: string;
  physicalVersion: string;
  financialPreviewVersion?: never;
}

export interface FinancialOrderSettlementCommand {
  requestKey: string;
  recognitionMonth?: string;
  physicalVersion: string;
  financialPreviewVersion: string;
}

export type OrderSettlementCommand =
  | OperationalOrderSettlementCommand
  | FinancialOrderSettlementCommand;

export type OrderSettlementCommandResolution =
  | { action: 'RETRY'; reason: string; financialPreviewVersion: string }
  | { action: 'SUPERSEDE'; reason: string };

interface LegacySettlementCommandBase {
  requestKey: string;
}

interface CorrectionOrderSettlementCommand extends LegacySettlementCommandBase {
  kind: 'CORRECTION';
  correctionOfId: string;
  recognizedAt: string;
  revenue: number;
  totalCost: number;
  note: string;
}

interface FinalOrderSettlementCommand extends LegacySettlementCommandBase {
  kind: 'FINAL';
  recognizedAt: string;
  note?: string | null;
  confirmStatusMismatch?: boolean;
  allowIncompleteDelivery?: boolean;
  incompleteDeliveryReason?: string;
}

type LegacyOrderSettlementCommand =
  | CorrectionOrderSettlementCommand
  | FinalOrderSettlementCommand;

export async function fetchOrderSettlementReadiness(orderId: string): Promise<OrderSettlementReadinessDto> {
  const response = await api.get(`/orders/${encodeURIComponent(orderId)}/settlement-readiness`);
  return normalizeOrderSettlementReadiness(response.data);
}

export async function fetchOrderSettlement(
  orderId: string,
  entity = 'orders.settlement',
): Promise<OrderSettlementDto> {
  const response = await api.get(`/orders/${encodeURIComponent(orderId)}/settlement`);
  return normalizeOrderSettlement(response.data, entity);
}

export async function submitOrderSettlementCommand(
  orderId: string,
  command: OrderSettlementCommand,
): Promise<OrderSettlementCommandSubmitResultDto> {
  const encodedOrderId = encodeURIComponent(orderId);
  // Runtime whitelist as well as the discriminated DTO: an optional `never`
  // property can still be supplied as `undefined` by untyped callers, but it
  // must not turn an OPERATIONS command into a finance-shaped request.
  const financialPreviewVersion = command.financialPreviewVersion;
  const payload = typeof financialPreviewVersion !== 'string'
    ? {
      requestKey: command.requestKey,
      physicalVersion: command.physicalVersion,
    }
    : {
      requestKey: command.requestKey,
      physicalVersion: command.physicalVersion,
      financialPreviewVersion,
    };
  const response = await api.post(`/orders/${encodedOrderId}/settlement-commands`, {
    ...payload,
    ...(command.recognitionMonth ? { recognitionMonth: command.recognitionMonth } : {}),
  });
  return normalizeOrderSettlementCommandSubmitResult(response.data);
}

export async function resolveOrderSettlementCommand(
  orderId: string,
  commandId: string,
  resolution: OrderSettlementCommandResolution,
): Promise<OrderSettlementCommandResultDto> {
  const encodedOrderId = encodeURIComponent(orderId);
  const encodedCommandId = encodeURIComponent(commandId);
  const payload = resolution.action === 'RETRY'
    ? {
      action: 'RETRY' as const,
      reason: resolution.reason.trim(),
      financialPreviewVersion: resolution.financialPreviewVersion,
    }
    : { action: 'SUPERSEDE' as const, reason: resolution.reason.trim() };
  const response = await api.post(
    `/orders/${encodedOrderId}/settlement-commands/${encodedCommandId}/resolve`,
    payload,
  );
  return normalizeOrderSettlementCommandResult(response.data);
}

/** Existing ADMIN ledger correction path, kept separately from the one-shot command. */
export async function submitLegacyOrderSettlementCommand(
  orderId: string,
  command: LegacyOrderSettlementCommand,
): Promise<unknown> {
  const encodedOrderId = encodeURIComponent(orderId);

  if (command.kind === 'FINAL') {
    const { kind: _kind, ...payload } = command;
    const response = await api.post(`/orders/${encodedOrderId}/settle`, payload);
    return response.data;
  }

  const response = await api.post(`/orders/${encodedOrderId}/settlements`, command);
  return response.data;
}
